import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormLabel,
  Radio,
  RadioGroup as RadioGroupBase,
  RadioGroupProps as RadioGroupBaseProp,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { ReactNode } from 'react';
import Typography from '@ui/Typography';

export type RadioGroupOption<Value extends string = string> = {
  value: Value;
  label: ReactNode;
  disabled?: boolean;
};

export interface RadioGroupProps<Value extends string = string>
  extends Pick<RadioGroupBaseProp, 'onChange'> {
  title?: string;
  groupAriaLabel: string;
  options: { 0: RadioGroupOption<Value>; 1: RadioGroupOption<Value> } & Array<
    RadioGroupOption<Value>
  >;
  labelPlacement?: Extract<
    FormControlLabelProps['labelPlacement'],
    'start' | 'end'
  >;
  classes?: { root?: string; group?: string };
  value?: Value;
}

const useStyles = makeStyles()(theme => ({
  groupLabel: {
    marginBottom: theme.spacing(2),
  },
  labelStart: {
    marginLeft: 0,
    justifyContent: 'space-between',
  },
  radioRoot: {
    padding: 2,
  },
  formControlLabelRoot: {
    gap: theme.spacing(1),
  },
}));

const RadioGroup = <Value extends string = string>(
  props: RadioGroupProps<Value>
) => {
  const { classes } = useStyles();
  const {
    title,
    groupAriaLabel,
    options,
    value,
    onChange,
    labelPlacement,
    classes: classNames,
  } = props;
  return (
    <FormControl component="fieldset" className={classNames?.root}>
      {title && (
        <FormLabel component="legend" className={classes.groupLabel}>
          {title}
        </FormLabel>
      )}
      <RadioGroupBase
        aria-label={groupAriaLabel}
        value={value}
        onChange={onChange}
        className={classNames?.group}
      >
        {options.map(({ value: val, label, disabled }) => (
          <FormControlLabel
            key={val}
            value={val}
            label={<Typography variant="pSm">{label}</Typography>}
            disabled={disabled}
            labelPlacement={labelPlacement}
            classes={{
              root: classes.formControlLabelRoot,
              labelPlacementStart: classes.labelStart,
            }}
            control={
              <Radio size="small" classes={{ root: classes.radioRoot }} />
            }
            disableTypography
          />
        ))}
      </RadioGroupBase>
    </FormControl>
  );
};

export default RadioGroup;
